import { id, Identifiable, IdentityProvider, Planzone } from "@planzone/dto";
import { HttpClient, HttpMethods } from "@planzone/services";

const prefix = "cas.authn.pac4j.";

function extractIndex(key: string): number {
  const res = key.match(/\d+/);

  return res && res.length > 0 ? parseInt(res[0]) || 0 : 0;
}

function extractProp(key: string): {
  name: string;
  providerName: string;
  index: number;
} {
  const elements = key.split(".");
  const name = elements[elements.length - 1];

  return {
    providerName: elements.length === 2 ? "cas" : elements[1] || "",
    name,
    index: elements.length > 0 ? extractIndex(elements[0]) : 0,
  };
}

function getIdentityProvidersFromAttr(attributes: {
  [key: string]: string;
}): Identifiable<IdentityProvider>[] {
  return Object.values(
    Object.entries(attributes)
      .filter(([key]) => key.slice(0, prefix.length) === prefix)
      .reduce<{
        [key: string]: Identifiable<IdentityProvider> & { index: number };
      }>((providers, [key, value]) => {
        const [_, ...rest] = key.split(prefix);
        const prop = extractProp(rest.join());
        const currentProvider = providers[prop.index] ?? {};
        const baseProvider = {
          ...currentProvider,
          name: prop.providerName,
        };

        return {
          ...providers,
          [prop.index]:
            prop.name === "uuid"
              ? {
                  ...baseProvider,
                  uuid: value,
                }
              : prop.name === "enable" || prop.name === "enabled"
                ? {
                    ...baseProvider,
                    isEnabled: value === "true",
                  }
                : {
                    ...baseProvider,
                    config: {
                      ...(providers[prop.index]
                        ? providers[prop.index].config
                        : {}),
                      [prop.name]: value,
                    },
                  },
        };
      }, {}),
  );
}

function filterDeletedProviders(
  identityProviders: Identifiable<IdentityProvider>[],
): Identifiable<IdentityProvider>[] {
  return identityProviders.filter((identity) => identity.uuid !== null);
}

function getIdentityProviders(httpClient: HttpClient) {
  return ({ uuid }: Identifiable<Planzone>) =>
    httpClient
      .sendHttpRequest<Identifiable<Planzone>>({
        endpoint: `/planzones/${uuid}`,
        method: HttpMethods.GET,
      })
      .then(({ attributes }) =>
        filterDeletedProviders(getIdentityProvidersFromAttr(attributes)),
      );
}

export { getIdentityProviders };
